import { Box, Drawer, Fade, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../shared";
import { Keypad, PinDots } from "./PinPad";

const PIN_LENGTH = 4;

interface RevealPinDrawerProps {
  open: boolean;
  onClose: () => void;
  /**
   * Отправляет введённый PIN на бэкенд. Промис резолвится при успехе,
   * реджектится с ошибкой бэкенда (например, 401 «wrong PIN» или 429
   * при rate-limit) — drawer сам покажет ошибку и очистит поле.
   */
  onSubmit: (pin: string) => Promise<void>;
  loading?: boolean;
}

/**
 * Модалка ввода PIN перед раскрытием реквизитов карты (PAN/CVV).
 *
 * Логика:
 *   - PIN спрашивается на **каждый** reveal — нет «сеансового» доверия;
 *   - при неверном PIN — красная подсветка точек, короткий feedback;
 *   - rate-limit брутфорса стоит на бэкенде (throttle + bcrypt) — здесь
 *     мы только показываем сообщение из ответа сервера.
 */
export function RevealPinDrawer({
  open,
  onClose,
  onSubmit,
  loading,
}: RevealPinDrawerProps) {
  const { t } = useTranslation("cards");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setPin("");
        setError(null);
        setSubmitting(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  const runSubmit = async (value: string) => {
    setSubmitting(true);
    try {
      await onSubmit(value);
      setPin("");
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || t("reveal_pin.wrong");
      setError(String(message));
      setTimeout(() => setPin(""), 500);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDigit = (d: string) => {
    if (submitting) return;
    if (error) setError(null);
    if (pin.length >= PIN_LENGTH) return;
    const next = pin + d;
    setPin(next);
    if (next.length === PIN_LENGTH) {
      void runSubmit(next);
    }
  };

  const handleBackspace = () => {
    if (submitting) return;
    if (error) setError(null);
    setPin((prev) => prev.slice(0, -1));
  };

  const busy = submitting || Boolean(loading);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={busy ? undefined : onClose}
      sx={{ zIndex: 100010 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
      }}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "10px 16px 28px",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={(theme) => ({
          width: "44px",
          height: "4px",
          borderRadius: "4px",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          margin: "0 auto 14px",
        })}
      />

      <Fade in timeout={200}>
        <Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 600,
              textAlign: "center",
              marginBottom: "4px",
              color: "text.primary",
            }}
          >
            {t("reveal_pin.title")}
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              color: error ? "#FF7A7A" : "text.secondary",
              textAlign: "center",
              marginBottom: "20px",
              minHeight: "18px",
              padding: "0 8px",
            }}
          >
            {error || t("reveal_pin.subtitle")}
          </Typography>
        </Box>
      </Fade>

      <PinDots length={PIN_LENGTH} filled={pin.length} error={Boolean(error)} />

      <Keypad
        onDigit={handleDigit}
        onBackspace={handleBackspace}
        disabled={busy}
      />

      <Button
        onClick={onClose}
        disableGlassEffect
        sx={(theme) => ({
          marginTop: "16px",
          bgcolor: "transparent",
          color: "text.secondary",
          "&:hover": {
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.04)"
                : "rgba(0, 0, 0, 0.04)",
          },
        })}
        disabled={busy}
      >
        {t("reveal_pin.cancel")}
      </Button>
    </Drawer>
  );
}
